import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  SLOT_PLANS,
  WELCOME_PROMO_CODE,
  addDaysToIsoDate,
  formatExpiryDisplay,
  getPlanById,
  planDaysForPlan,
} from "constants/clientSlotPlans";
import { apiUrl } from "utils/apiUrl";
import { paymentApiUrl } from "utils/paymentApiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { clearPaymentConfigCache, loadPaymentConfig } from "utils/paymentConfig";
import {
  clearPendingPayment,
  clearPostPayRedirect,
  formatCountdown,
  loadPendingPayment,
  loadPostPayRedirect,
  PENDING_DURATION_MS,
  resolvePostPayRedirectAt,
  savePendingPayment,
} from "utils/slotPaymentSession";

/** USDT (BEP20) on BSC — standard contract, 40 hex chars after 0x. */
function looksLikeBscAddress(addr) {
  return /^0x[a-fA-F0-9]{40}$/.test(String(addr).trim());
}

function looksLikeSolanaAddress(addr) {
  const s = String(addr || "").trim();
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(s) && !s.startsWith("0x");
}

/** Libellés réseau selon payment-config (Plisio USDT_BSC / USDT_SOL). */
function paymentNetworkCopy(networkLabel, plisioCurrency) {
  const cur = String(plisioCurrency || "").toUpperCase();
  const label = String(networkLabel || "");
  const isSol =
    cur === "USDT_SOL" || /solana|spl/i.test(label) || /solana|spl/i.test(cur);
  if (isSol) {
    return {
      isSol: true,
      checkoutLine: "USDT Solana (SPL)",
      instructionsTitle: "Instructions — USDT Solana (SPL)",
      chainStrong: "Solana (SPL)",
      wrongNetworks: "BSC (BEP20), Tron (TRC20), or Ethereum (ERC20)",
      addressLine: "Solana wallet address (base58, not 0x)",
      depositLabel: "Deposit address (Solana)",
      singleChain: "Solana",
    };
  }
  return {
    isSol: false,
    checkoutLine: "USDT BEP20 (BSC)",
    instructionsTitle: "Instructions — USDT BEP20 (BSC)",
    chainStrong: "BEP20 (BSC)",
    wrongNetworks: "Tron (TRC20) or Ethereum (ERC20)",
    addressLine: "0x followed by 40 hex characters, one line",
    depositLabel: "Deposit address (BEP20)",
    singleChain: "BEP20",
  };
}

function normalizePromo(raw) {
  return String(raw || "").trim().replace(/\s+/g, "").toUpperCase();
}

function pickPlanFromLocationState(state) {
  if (state?.planId && SLOT_PLANS.some((p) => p.id === state.planId)) {
    return state.planId;
  }
  const match = SLOT_PLANS.find(
    (p) => Number(p.priceUsdRegular) === Number(state?.regularPriceUsd),
  );
  return match?.id || SLOT_PLANS[0].id;
}

function checkoutSubscriptionPath(applied, searchParams) {
  const fromUrl = String(searchParams?.get("path") || "")
    .trim()
    .toLowerCase();
  if (fromUrl === "path1" || fromUrl === "path2") return fromUrl;
  if (applied?.subscriptionPath === "path1" || applied?.subscriptionPath === "path2") {
    return applied.subscriptionPath;
  }
  if (applied?.kind === "path1") return "path1";
  if (applied?.kind === "path2") return "path2";
  return undefined;
}

function formatUsdtAmount(n) {
  const v = Math.round(Number(n));
  return Number.isFinite(v) && v > 0 ? String(v) : "0";
}

function SlotNameBadge({ name, className = "" }) {
  if (!name) return null;
  return (
    <div
      className={`rounded-xl border border-brand-400/35 bg-brand-500/10 px-4 py-2.5 text-center ${className}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        Your slot
      </p>
      <p className="mt-0.5 font-mono text-lg font-bold text-brand-200">{name}</p>
    </div>
  );
}

function buildSessionFromPayload({
  planId,
  regularPriceUsd,
  priceUsd,
  planPriceUsd: planPriceUsdField,
  sendAmountUsdt,
  walletSendBufferUsdt,
  plisioUnlockMinUsdt,
  bep20,
  mock,
  slotName,
}) {
  const checkoutUrl =
    (typeof bep20?.checkoutUrl === "string" && bep20.checkoutUrl) || "";
  const payAddr =
    typeof bep20?.payAddress === "string" ? bep20.payAddress.trim() : "";
  if (!bep20 || bep20.payAmount == null || (!payAddr && !checkoutUrl)) {
    return null;
  }
  const planPrice = Number.isFinite(Number(planPriceUsdField ?? priceUsd))
    ? Math.round(Number(planPriceUsdField ?? priceUsd))
    : 35;
  const feeBuffer = Number.isFinite(Number(walletSendBufferUsdt))
    ? Math.min(2, Math.max(0, Math.floor(Number(walletSendBufferUsdt))))
    : 2;
  const sendAmount = Number.isFinite(Number(sendAmountUsdt))
    ? Math.round(Number(sendAmountUsdt))
    : planPrice + feeBuffer;
  const minReceived = planPrice;
  if (!Number.isFinite(sendAmount) || sendAmount < 1) return null;
  const slot = String(slotName || "").trim();
  return {
    planId: planId || null,
    slotName: slot || undefined,
    amountBep20: formatUsdtAmount(minReceived),
    sendAmountUsdt: formatUsdtAmount(sendAmount),
    walletSendBufferUsdt: feeBuffer,
    regularPriceUsd: Number.isFinite(Number(regularPriceUsd))
      ? Number(regularPriceUsd)
      : 50,
    planPriceUsd: planPrice,
    plisioUnlockMinUsdt:
      Number.isFinite(Number(plisioUnlockMinUsdt)) && Number(plisioUnlockMinUsdt) > 0
        ? Number(plisioUnlockMinUsdt)
        : null,
    mock: Boolean(mock),
    bep20: {
      paymentId: bep20.paymentId,
      payAddress: payAddr.replace(/\s+/g, ""),
      payAmount: bep20.payAmount,
      payCurrency: bep20.payCurrency || "usdtbsc",
      plisioEmbedded: Boolean(bep20.plisioEmbedded && payAddr),
      qrCode: typeof bep20.qrCode === "string" ? bep20.qrCode : null,
      checkoutUrl: checkoutUrl || bep20.checkoutUrl,
      network: bep20.network || "BEP20 (BSC)",
    },
  };
}

export default function PayUsdt() {
  const navigate = useNavigate();
  const { state, pathname, search } = useLocation();
  const payReturnTo = `${pathname}${search}`;
  const signInHref = `/SignIn?returnTo=${encodeURIComponent(payReturnTo)}`;
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState("");

  const [session, setSession] = useState(() =>
    state
      ? buildSessionFromPayload({
          planId: state.planId || pickPlanFromLocationState(state),
          regularPriceUsd: state.regularPriceUsd,
          priceUsd: state.priceUsd,
          planPriceUsd: state.planPriceUsd ?? state.priceUsd,
          sendAmountUsdt: state.sendAmountUsdt,
          walletSendBufferUsdt: state.walletSendBufferUsdt,
          plisioUnlockMinUsdt: state.plisioUnlockMinUsdt,
          bep20: state.bep20,
          mock: state.mock,
          slotName: state.slotName || state.reservedOrderId || "",
        })
      : null,
  );

  const [selectedPlanId, setSelectedPlanId] = useState(() =>
    state ? pickPlanFromLocationState(state) : SLOT_PLANS[0].id,
  );

  const [promoDraft, setPromoDraft] = useState(() => String(state?.promo || ""));
  const [applied, setApplied] = useState(() =>
    state?.promo
      ? {
          code: String(state.promo),
          discountPercent: Number(state.discountPercent) || 0,
          kind: state.kind || "promo",
          pathLabel: state.pathLabel || "Promo",
          source: state.source || null,
          infoMessage: state.appliedMessage || "Promo code applied.",
        }
      : null,
  );
  const [promoError, setPromoError] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(() => !state);
  const [confirmingMock, setConfirmingMock] = useState(false);
  const [mockMsg, setMockMsg] = useState("");
  const [mockError, setMockError] = useState("");
  const [awaitingTransfer, setAwaitingTransfer] = useState(false);
  const [pendingStartedAt, setPendingStartedAt] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [actuallyPaidUsdt, setActuallyPaidUsdt] = useState(null);
  const [postPayRedirectAt, setPostPayRedirectAt] = useState(null);

  const [now, setNow] = useState(() => Date.now());
  const [paymentCfg, setPaymentCfg] = useState(null);

  useEffect(() => {
    clearPaymentConfigCache();
    void loadPaymentConfig().then(setPaymentCfg);
  }, []);

  const active = session?.bep20 || null;
  const paymentId = active?.paymentId;

  useEffect(() => {
    if (!paymentId) return;
    setPaymentStatus((prev) =>
      prev === "confirmed" || prev === "awaiting_approval" ? prev : "pending",
    );
    setGatewayStatus(null);
    setActuallyPaidUsdt(null);
  }, [paymentId]);
  const sendAmountLabel = session?.sendAmountUsdt || String(session?.planPriceUsd ?? "");
  const planPriceLabel = String(session?.planPriceUsd ?? "");
  const providerLabel = paymentCfg?.providerLabel || "Plisio";
  const providerId = paymentCfg?.provider || "plisio";
  const networkFeeMax =
    session?.walletSendBufferUsdt ??
    (providerId === "plisio" ? 1 : 2);
  const discountUsd = session
    ? Math.max(0, session.regularPriceUsd - session.planPriceUsd)
    : 0;
  const discountPct =
    session && session.regularPriceUsd > 0
      ? Math.round((discountUsd / session.regularPriceUsd) * 1000) / 10
      : 0;

  const slotPaymentPath =
    paymentCfg?.slotPaymentEndpoint || "/api/client/slot-payment";
  const checkoutUrl =
    (typeof active?.checkoutUrl === "string" && active.checkoutUrl) || "";
  const net = useMemo(
    () =>
      paymentNetworkCopy(
        active?.network || paymentCfg?.networks?.[0],
        paymentCfg?.plisioCurrency,
      ),
    [active?.network, paymentCfg],
  );
  const isSolNetwork = net.isSol;
  const addressOk =
    looksLikeBscAddress(active?.payAddress) ||
    (isSolNetwork && looksLikeSolanaAddress(active?.payAddress)) ||
    Boolean(checkoutUrl);

  const plisioEmbedded =
    providerId === "plisio" &&
    Boolean(active?.plisioEmbedded && active?.payAddress);
  const qrImageUrl = active
    ? active.qrCode && String(active.qrCode).startsWith("data:image")
      ? active.qrCode
      : active.payAddress
        ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
            active.payAddress,
          )}`
        : null
    : null;


  async function copyText(label, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("");
    }
  }

  const checkoutPath = useMemo(
    () => checkoutSubscriptionPath(applied, searchParams),
    [applied, searchParams],
  );

  const extendMode = useMemo(
    () =>
      state?.extendMode === true ||
      String(searchParams.get("extend") || "").trim() === "1" ||
      String(searchParams.get("extend") || "").toLowerCase() === "true",
    [searchParams, state?.extendMode],
  );

  const extendSlotNum = useMemo(() => {
    const fromState = Number(state?.extendSlot);
    if (Number.isFinite(fromState) && fromState >= 1) return fromState;
    const n = Number(searchParams.get("slot"));
    return Number.isFinite(n) && n >= 1 ? n : null;
  }, [searchParams, state?.extendSlot]);

  const currentExpiringDate = useMemo(() => {
    const fromState = String(state?.currentExpiringDate || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromState)) return fromState;
    const fromUrl = String(searchParams.get("currentExp") || "").trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(fromUrl) ? fromUrl : null;
  }, [searchParams, state?.currentExpiringDate]);

  const selectedPlan = useMemo(
    () => getPlanById(selectedPlanId) || SLOT_PLANS[0],
    [selectedPlanId],
  );

  const projectedExpiry = useMemo(() => {
    if (!extendMode || !currentExpiringDate) return null;
    return addDaysToIsoDate(
      currentExpiringDate,
      planDaysForPlan(selectedPlan),
    );
  }, [extendMode, currentExpiringDate, selectedPlan]);

  const postPayDestination = useMemo(() => {
    if (checkoutPath === "path1") {
      return "/client/subscriptions/Path1BrokerPartner";
    }
    if (checkoutPath === "path2") {
      return "/client/subscriptions/Path2PersonnalBroker";
    }
    return "/client/dashboard";
  }, [checkoutPath]);

  const slotName = useMemo(() => {
    const fromSession = String(session?.slotName || "").trim();
    if (fromSession) return fromSession;
    const fromUrl = String(searchParams.get("reserve") || "").trim();
    if (fromUrl) return fromUrl;
    const stored = loadPendingPayment();
    return String(stored?.session?.slotName || "").trim();
  }, [session?.slotName, searchParams]);

  const extendChooserUrl = useMemo(() => {
    if (!extendMode || !extendSlotNum) return null;
    const qs = new URLSearchParams();
    qs.set("slot", String(extendSlotNum));
    if (slotName) qs.set("reserve", slotName);
    if (checkoutPath) qs.set("path", checkoutPath);
    const promo = applied?.code || searchParams.get("promo");
    if (promo) qs.set("promo", promo);
    if (currentExpiringDate) qs.set("currentExp", currentExpiringDate);
    return `/ExtendSlot?${qs.toString()}`;
  }, [
    extendMode,
    extendSlotNum,
    slotName,
    checkoutPath,
    applied?.code,
    searchParams,
    currentExpiringDate,
  ]);

  const exitAwaitingTransfer = useCallback(() => {
    setAwaitingTransfer(false);
    setPendingStartedAt(null);
    setPostPayRedirectAt(null);
    setPaymentStatus("pending");
    clearPendingPayment();
    clearPostPayRedirect();
  }, []);

  const pendingEndsAt =
    pendingStartedAt != null ? pendingStartedAt + PENDING_DURATION_MS : null;
  const msLeft =
    pendingEndsAt != null ? Math.max(0, pendingEndsAt - now) : PENDING_DURATION_MS;
  const pendingExpired = awaitingTransfer && msLeft <= 0;

  const fetchPaymentStatus = useCallback(async (overridePaymentId) => {
    const pid = String(overridePaymentId || paymentId || "").trim();
    const reserve = String(
      searchParams.get("reserve") || session?.slotName || "",
    ).trim();
    if (!pid && !reserve) return null;
    try {
      const syncQs = new URLSearchParams();
      if (pid) syncQs.set("paymentId", pid);
      if (reserve) syncQs.set("reserve", reserve);
      const syncRes = await fetch(
        paymentApiUrl(`/api/client/slot-payment-sync?${syncQs}`),
        { credentials: "include", cache: "no-store" },
      );
      const syncData = await syncRes.json().catch(() => ({}));

      const statusQs = new URLSearchParams();
      if (pid) statusQs.set("paymentId", pid);
      if (reserve) statusQs.set("reserve", reserve);
      const res = await fetch(
        apiUrl(`/api/client/slot-payment-status?${statusQs}`),
        { credentials: "include", cache: "no-store" },
      );
      const data = await res.json().catch(() => ({}));

      const syncStatus =
        syncData.ok && syncData.status
          ? String(syncData.status).toLowerCase()
          : null;
      const statusStatus =
        res.ok && data.ok && data.status
          ? String(data.status).toLowerCase()
          : null;

      let s = statusStatus || syncStatus || "pending";
      const paid = Number(
        data.actuallyPaid ?? syncData.received ?? syncData.actuallyPaid,
      );
      const min = Number(data.unlockMinUsdt);
      if (Number.isFinite(paid) && paid > 0) {
        setActuallyPaidUsdt(paid);
      }
      if (
        (s === "pending" || s === "unknown") &&
        syncStatus &&
        (syncStatus === "confirmed" || syncStatus === "awaiting_approval")
      ) {
        s = syncStatus;
      }
      if (
        s === "confirmed" &&
        min > 0 &&
        (!Number.isFinite(paid) || paid + 0.0001 < min)
      ) {
        s = "pending";
      }
      setPaymentStatus(s);
      const gw = data.gatewayStatus || syncData.gatewayStatus;
      if (gw) {
        setGatewayStatus(String(gw).toLowerCase());
      }
      return s;
    } catch {
      return null;
    }
  }, [paymentId, searchParams, session?.slotName]);

  const pendingStatusLabel = useMemo(() => {
    if (paymentStatus === "confirmed") {
      return "Payment confirmed — unlocking your slot…";
    }
    if (paymentStatus === "awaiting_approval") {
      return "Payment received — Waiting for Admin Approval…";
    }
    if (paymentStatus === "cancelled" || paymentStatus === "canceled") {
      return "Payment cancelled — time expired";
    }
    if (
      paymentStatus === "pending" &&
      actuallyPaidUsdt != null &&
      Number(session?.plisioUnlockMinUsdt ?? session?.amountBep20) > 0 &&
      actuallyPaidUsdt + 0.0001 >=
        Number(session?.plisioUnlockMinUsdt ?? session?.amountBep20)
    ) {
      return "Payment detected on chain — confirming…";
    }
    if (paymentStatus === "failed") {
      return `Payment failed — send exactly ${sendAmountLabel} USDT; we need at least ${session?.amountBep20 ?? "33"} USDT received (max ${networkFeeMax} USDT network fee).`;
    }
    if (pendingExpired) return "Time expired — start a new payment";
    return "Waiting for payment…";
  }, [paymentStatus, pendingExpired, actuallyPaidUsdt, sendAmountLabel, session?.amountBep20, session?.plisioUnlockMinUsdt, networkFeeMax]);

  const ensureApiSession = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/api/client/session"), {
        credentials: "include",
        cache: "no-store",
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const regenerate = useCallback(async ({ planId, promo, subscriptionPath }) => {
    setRegenerating(true);
    setRegenError(null);
    try {
      const apiOk = await ensureApiSession();
      if (!apiOk) {
        setRegenError(
          "You are signed in on this page, but the payment server did not receive your session cookie. Sign out, sign in again, then open Buy a slot.",
        );
        return false;
      }

      const reservedOrderId = String(
        searchParams.get("reserve") || session?.slotName || "",
      ).trim();
      const body = { planId, promo: promo || "" };
      if (subscriptionPath) body.subscriptionPath = subscriptionPath;
      if (reservedOrderId && !extendMode) body.reservedOrderId = reservedOrderId;
      if (extendMode && extendSlotNum) {
        body.extendSlot = true;
        body.extend = "1";
        body.slot = extendSlotNum;
      }
      const res = await fetch(paymentApiUrl(slotPaymentPath), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        let fallback = "Payment could not be started. Please try again.";
        if (res.status === 401) {
          fallback =
            "Payment server session expired. Sign out, sign in again, then retry.";
        } else if (res.status === 404) {
          fallback =
            "Payment route missing on the API — backend on the VPS must be updated.";
        }
        setRegenError(clientSafeMessage(data.message, fallback));
        return false;
      }
      const next = buildSessionFromPayload({
        planId: data.planId || planId,
        regularPriceUsd: data.regularPriceUsd,
        priceUsd: data.priceUsd,
        sendAmountUsdt: data.sendAmountUsdt,
        planPriceUsd: data.planPriceUsd ?? data.priceUsd,
        walletSendBufferUsdt: data.walletSendBufferUsdt,
        plisioUnlockMinUsdt: data.plisioUnlockMinUsdt,
        bep20: data.bep20,
        mock: data.mock,
        slotName: String(
          data.slotOrderId ||
            reservedOrderId ||
            (extendMode && extendSlotNum && searchParams.get("reserve")
              ? String(searchParams.get("reserve")).trim()
              : "") ||
            "",
        ).trim(),
      });
      if (!next) {
        setRegenError("Invalid payment data returned.");
        return false;
      }
      if (data.promo) {
        setApplied((prev) => ({
          code: String(data.promo),
          discountPercent: Number(data.discountPercent) || 0,
          kind: prev?.kind || "promo",
          subscriptionPath: prev?.subscriptionPath || null,
          pathLabel: prev?.pathLabel || "Promo",
          source: prev?.source || null,
          infoMessage: "Promo code applied. Prices below reflect this discount.",
        }));
        setPromoDraft(String(data.promo));
      }
      setSession(next);
      const reserveLabel = String(
        data.slotOrderId || reservedOrderId || next.slotName || "",
      ).trim();
      const sessionToStore = reserveLabel
        ? { ...next, slotName: reserveLabel }
        : next;
      savePendingPayment(sessionToStore);
      if (awaitingTransfer && next.bep20?.paymentId !== session?.bep20?.paymentId) {
        exitAwaitingTransfer();
      }
      return true;
    } catch (e) {
      setRegenError(clientSafeMessage(e?.message, "Network error."));
      return false;
    } finally {
      setRegenerating(false);
    }
  }, [
    awaitingTransfer,
    session?.bep20?.paymentId,
    session?.slotName,
    exitAwaitingTransfer,
    ensureApiSession,
    slotPaymentPath,
    searchParams,
    extendMode,
    extendSlotNum,
  ]);

  const proceedToPay = useCallback(() => {
    if (!session?.amountBep20) return;
    if (checkoutUrl) {
      window.open(checkoutUrl, "_blank", "noopener,noreferrer");
    }
    if (!session?.bep20?.payAddress && !checkoutUrl) return;

    setPaymentStatus("pending");
    setGatewayStatus(null);
    setAwaitingTransfer(true);
    setPendingStartedAt(Date.now());
    const sessionWithSlot = {
      ...session,
      slotName:
        session.slotName ||
        String(searchParams.get("reserve") || "").trim() ||
        undefined,
    };
    if (sessionWithSlot.slotName && sessionWithSlot.slotName !== session.slotName) {
      setSession(sessionWithSlot);
    }
    savePendingPayment(sessionWithSlot);
    void fetchPaymentStatus(session.bep20?.paymentId);
  }, [session, checkoutUrl, fetchPaymentStatus, searchParams]);

  const validatePromo = useCallback(
    async (rawCode) => {
      const trimmed = String(rawCode ?? "").trim();
      const pathForCheckout = checkoutSubscriptionPath(applied, searchParams);
      if (!trimmed) {
        setPromoError(null);
        if (applied) {
          setApplied(null);
          await regenerate({
            planId: selectedPlanId,
            promo: "",
            subscriptionPath: pathForCheckout,
          });
        }
        return false;
      }
      setPromoError(null);
      setApplyingPromo(true);
      try {
        const validateBody = { code: trimmed };
        if (pathForCheckout) validateBody.subscriptionPath = pathForCheckout;
        const res = await fetch(apiUrl("/api/client/validate-slot-promo"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(validateBody),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          setApplied(null);
          setPromoError(
            clientSafeMessage(
              data.message,
              res.status === 401
                ? "Sign in to validate a promo code."
                : "This promo code is not valid.",
            ),
          );
          return false;
        }
        const nextApplied = {
          code: data.code,
          discountPercent: data.discountPercent,
          kind: data.kind,
          subscriptionPath: data.subscriptionPath || null,
          pathLabel: data.pathLabel,
          source: data.source,
          infoMessage: data.message || "Promo code applied.",
        };
        setApplied(nextApplied);
        setPromoDraft(String(data.code ?? trimmed ?? ""));
        setApplyingPromo(false);
        await regenerate({
          planId: selectedPlanId,
          promo: nextApplied.code,
          subscriptionPath:
            pathForCheckout ||
            nextApplied.subscriptionPath ||
            (nextApplied.kind === "path1"
              ? "path1"
              : nextApplied.kind === "path2"
                ? "path2"
                : undefined),
        });
        return true;
      } catch (e) {
        setApplied(null);
        setPromoError(clientSafeMessage(e?.message, "Network error."));
        return false;
      } finally {
        setApplyingPromo(false);
      }
    },
    [applied, searchParams, selectedPlanId, regenerate],
  );

  const confirmMockPayment = useCallback(async () => {
    if (!session?.bep20?.paymentId) return;
    setConfirmingMock(true);
    setMockMsg("");
    setMockError("");
    try {
      const res = await fetch(paymentApiUrl("/api/dev/mock-confirm-payment"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentId: session.bep20.paymentId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setMockError(
          clientSafeMessage(
            data.message,
            res.status === 403
              ? "Test payment confirmation is not available."
              : "Payment confirmation failed. Please try again.",
          ),
        );
        return;
      }
      setMockMsg(
        extendMode
          ? "Extension payment confirmed (mock). Your subscription expiry will update shortly."
          : "Payment confirmed (mock). Your slot is now available.",
      );
    } catch (e) {
      setMockError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setConfirmingMock(false);
    }
  }, [session, extendMode]);

  const handlePlanChange = useCallback(
    (newPlanId) => {
      if (!newPlanId || newPlanId === selectedPlanId) return;
      setSelectedPlanId(newPlanId);
      void regenerate({
        planId: newPlanId,
        promo: applied?.code || searchParams.get("promo") || "",
        subscriptionPath: checkoutPath,
      });
    },
    [selectedPlanId, applied, searchParams, checkoutPath, regenerate],
  );

  /** Reprise après refresh uniquement — pas au premier arrivage depuis Buy slot. */
  useEffect(() => {
    if (!paymentId || !session?.bep20?.paymentId) return;
    const stored = loadPendingPayment();
    if (stored?.session?.bep20?.paymentId !== paymentId || !stored.startedAt) {
      return;
    }
    const endsAt = stored.startedAt + PENDING_DURATION_MS;
    if (Date.now() >= endsAt) {
      clearPendingPayment();
      clearPostPayRedirect();
      return;
    }
    setAwaitingTransfer(true);
    setPendingStartedAt(stored.startedAt);
    const redirectKey = String(
      paymentId || stored.session?.slotName || "",
    ).trim();
    if (redirectKey) {
      setPostPayRedirectAt(
        resolvePostPayRedirectAt({
          startedAt: stored.startedAt,
          key: redirectKey,
          destination: postPayDestination,
          slotName: stored.session?.slotName || slotName || null,
        }),
      );
    }
    if (stored.session?.slotName && stored.session.slotName !== session?.slotName) {
      setSession((prev) =>
        prev ? { ...prev, slotName: stored.session.slotName } : prev,
      );
    }
    void fetchPaymentStatus();
  }, [paymentId, session?.bep20?.paymentId, session?.slotName, fetchPaymentStatus, postPayDestination, slotName]);

  useEffect(() => {
    if (!pendingStartedAt || !awaitingTransfer) return;
    const redirectKey = String(paymentId || slotName || "").trim();
    if (!redirectKey) return;
    setPostPayRedirectAt(
      resolvePostPayRedirectAt({
        startedAt: pendingStartedAt,
        key: redirectKey,
        destination: postPayDestination,
        slotName: slotName || null,
      }),
    );
  }, [
    pendingStartedAt,
    awaitingTransfer,
    paymentId,
    slotName,
    postPayDestination,
  ]);

  useEffect(() => {
    if (!awaitingTransfer) return undefined;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [awaitingTransfer]);

  useEffect(() => {
    if (!pendingExpired) return;
    if (
      paymentStatus === "confirmed" ||
      paymentStatus === "awaiting_approval" ||
      paymentStatus === "cancelled"
    ) {
      return;
    }
    void fetchPaymentStatus(paymentId || undefined);
  }, [pendingExpired, paymentStatus, paymentId, fetchPaymentStatus]);

  useEffect(() => {
    const reserve = String(
      searchParams.get("reserve") || session?.slotName || "",
    ).trim();
    if ((!paymentId && !reserve) || session?.mock || bootstrapping) return undefined;
    void fetchPaymentStatus(paymentId || undefined);
    const pollId = window.setInterval(async () => {
      const s = await fetchPaymentStatus(paymentId || undefined);
      if (
        s === "confirmed" ||
        s === "awaiting_approval" ||
        s === "failed" ||
        s === "refunded"
      ) {
        window.clearInterval(pollId);
      }
    }, 2000);
    return () => window.clearInterval(pollId);
  }, [
    paymentId,
    session?.mock,
    session?.slotName,
    bootstrapping,
    fetchPaymentStatus,
    searchParams,
  ]);

  useEffect(() => {
    const paidDetected =
      paymentStatus === "confirmed" || paymentStatus === "awaiting_approval";
    if (!paidDetected) return undefined;

    const redirectKey = String(paymentId || slotName || "").trim();
    let redirectAt = postPayRedirectAt;
    if (!redirectAt && pendingStartedAt && redirectKey) {
      redirectAt = resolvePostPayRedirectAt({
        startedAt: pendingStartedAt,
        key: redirectKey,
        destination: postPayDestination,
        slotName: slotName || null,
      });
      if (redirectAt) setPostPayRedirectAt(redirectAt);
    }
    if (!redirectAt) {
      // Fallback: payment detected but no timer — go now.
      clearPendingPayment();
      clearPostPayRedirect();
      navigate(postPayDestination, {
        replace: true,
        state: extendMode
          ? { subscriptionExtended: true, slotName: slotName || null }
          : paymentStatus === "confirmed"
            ? { paymentConfirmed: true, slotName: slotName || null }
            : {
                paymentPendingApproval: true,
                slotName: slotName || null,
              },
      });
      return undefined;
    }

    const destination =
      loadPostPayRedirect()?.destination || postPayDestination;

    const go = () => {
      if (Date.now() < redirectAt) return;
      clearPendingPayment();
      clearPostPayRedirect();
      navigate(destination, {
        replace: true,
        state: extendMode
          ? { subscriptionExtended: true, slotName: slotName || null }
          : paymentStatus === "confirmed"
            ? { paymentConfirmed: true, slotName: slotName || null }
            : {
                paymentPendingApproval: true,
                slotName: slotName || null,
              },
      });
    };

    go();
    const id = window.setInterval(go, 1000);
    return () => window.clearInterval(id);
  }, [
    paymentStatus,
    postPayRedirectAt,
    postPayDestination,
    slotName,
    navigate,
    extendMode,
    paymentId,
    pendingStartedAt,
  ]);

  useEffect(() => {
    if (!bootstrapping || state) return;
    if (String(searchParams.get("extend") || "").trim() === "1") {
      const qs = new URLSearchParams(searchParams);
      qs.delete("extend");
      qs.delete("plan");
      navigate(`/ExtendSlot?${qs.toString()}`, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      const reserveFromUrl = String(searchParams.get("reserve") || "").trim();
      const stored = loadPendingPayment();
      const storedReserve = String(stored?.session?.slotName || "").trim();
      const sameReserve =
        !reserveFromUrl || !storedReserve || storedReserve === reserveFromUrl;
      if (stored?.session?.bep20?.paymentId && sameReserve) {
        setSession(stored.session);
        if (stored.startedAt) {
          setAwaitingTransfer(true);
          setPendingStartedAt(stored.startedAt);
          const redirectKey = String(
            stored.session?.bep20?.paymentId ||
              stored.session?.slotName ||
              reserveFromUrl ||
              "",
          ).trim();
          if (redirectKey) {
            setPostPayRedirectAt(
              resolvePostPayRedirectAt({
                startedAt: stored.startedAt,
                key: redirectKey,
                destination: postPayDestination,
                slotName: stored.session?.slotName || reserveFromUrl || null,
              }),
            );
          }
        }
        if (!cancelled) setBootstrapping(false);
        return;
      }
      const promoFromUrl = String(searchParams.get("promo") || "").trim();
      const planFromUrl = String(searchParams.get("plan") || "").trim();
      const planId =
        !extendMode &&
        planFromUrl &&
        SLOT_PLANS.some((p) => p.id === planFromUrl)
          ? planFromUrl
          : selectedPlanId;
      if (planId !== selectedPlanId) {
        setSelectedPlanId(planId);
      }
      await regenerate({
        planId,
        promo: promoFromUrl || applied?.code || "",
        subscriptionPath: checkoutSubscriptionPath(applied, searchParams),
      });
      if (!cancelled) setBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapping]);

  if (!session) {
    if (bootstrapping || regenerating) {
      const reserveLoading = String(searchParams.get("reserve") || "").trim();
      return (
        <main className="min-h-screen bg-[#0B1437] px-5 py-16 text-center text-white sm:px-6">
          {reserveLoading ? (
            <SlotNameBadge name={reserveLoading} className="mx-auto mb-4 max-w-xs" />
          ) : null}
          <p className="text-gray-300">
            {extendMode ? "Preparing your extension payment…" : "Preparing your payment…"}
          </p>
        </main>
      );
    }
    return (
      <main className="min-h-screen bg-[#0B1437] px-5 py-16 text-center text-white sm:px-6">
        <p className="text-gray-300">
          {regenError || "Unable to start a payment session."}
        </p>
        <Link
          to={signInHref}
          className="mt-6 inline-block font-medium text-brand-300 underline-offset-2 hover:underline"
        >
          Go to Sign In
        </Link>
      </main>
    );
  }

  const hideManualWallet = !plisioEmbedded;

  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <header className="text-center">
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
            {extendMode
              ? awaitingTransfer
                ? "Extension payment pending"
                : "Extend subscription"
              : awaitingTransfer
                ? "Payment pending"
                : "Pay with USDT"}
          </h1>
          <p className="mt-1 text-[10px] font-mono text-gray-600" aria-hidden="true">
            {`checkout-ui-v4-${providerId}`}
          </p>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-300">
            {extendMode && !awaitingTransfer ? (
              <>
                Extend your slot subscription — pay with{" "}
                <strong className="text-white">{net.checkoutLine}</strong>. The new
                expiration date is applied after payment confirmation and admin approval.
              </>
            ) : awaitingTransfer ? (
              plisioEmbedded ? (
                <>
                  Send <strong className="text-white">{sendAmountLabel} USDT</strong> to the address
                  below on <strong className="text-white">{net.chainStrong}</strong>. Stay on this
                  page while we confirm.
                </>
              ) : (
                <>
                  Send exactly{" "}
                  <strong className="text-white">{sendAmountLabel} USDT</strong> on{" "}
                  <strong className="text-white">{net.chainStrong}</strong>. If more than{" "}
                  {networkFeeMax} USDT is taken as network fee, the payment will fail. Stay on this
                  page.
                </>
              )
            ) : (
              <>
                Crypto checkout via <strong className="text-white">{providerLabel}</strong> —{" "}
                <strong className="text-white">{net.checkoutLine}</strong> only. Send{" "}
                <strong>{sendAmountLabel} USDT</strong>
                {plisioEmbedded
                  ? ` (plan ${planPriceLabel} USDT + ~1 USDT BEP20 BSC Network fee — not ${planPriceLabel}).`
                  : ` total so at least ${session?.planPriceUsd ?? sendAmountLabel} USDT arrives after fees.`}
              </>
            )}
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:items-start">
          <aside className="order-last space-y-4 lg:order-first lg:sticky lg:top-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-sm text-gray-300 shadow-lg">
              <h2 className="text-base font-semibold text-white">{net.instructionsTitle}</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                <li>Choose your subscription.</li>
                <li>Redeem your promo code (if you have one) and click Apply.</li>
                <li>
                  Network: <strong>{net.chainStrong}</strong> — not {net.wrongNetworks}.
                </li>
                {plisioEmbedded ? (
                  <li>
                    Copy the <strong>deposit address</strong> on the right and pay in your wallet
                    (RedotPay, MetaMask, etc.).
                  </li>
                ) : checkoutUrl ? (
                  <li>
                    Click the payment button and complete checkout on {providerLabel}.
                  </li>
                ) : (
                  <li>
                    Address:{" "}
                    {net.isSol ? (
                      <span>{net.addressLine}</span>
                    ) : (
                      <>
                        <code className="text-brand-200">0x</code> followed by 40 hex characters, one
                        line
                      </>
                    )}
                  </li>
                )}
                <li>
                  Amount: exactly <strong>{sendAmountLabel} USDT</strong>
                  {plisioEmbedded
                    ? ` (plan ${planPriceLabel} + 1 USDT BEP20 BSC Network Fee — do not send only ${planPriceLabel}).`
                    : ` (min. ${session?.amountBep20 ?? session?.planPriceUsd} USDT received).`}
                </li>
              </ul>
              <p className="mt-3 border-t border-white/10 pt-3 text-xs text-amber-200/90">
                Send only <strong>one</strong> payment on <strong>{net.singleChain}</strong>. Do not use{" "}
                {net.wrongNetworks} for this transaction.
              </p>
              {!plisioEmbedded ? (
                <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                  Enter <strong>{sendAmountLabel} USDT</strong> in your wallet (plan{" "}
                  {session?.planPriceUsd ?? "?"}
                  + up to {networkFeeMax} USDT network fee). Slot unlocks when at least{" "}
                  <strong>{session?.planPriceUsd ?? session?.amountBep20} USDT</strong> is received
                  — checked every 2 minutes.
                </p>
              ) : null}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-sm text-gray-300 shadow-lg">
              <h2 className="text-base font-semibold text-white">Public promo codes</h2>
              <p className="mt-2 text-xs text-gray-400">
                Click a code to apply it to your payment.
              </p>
              <ul className="mt-3 space-y-2">
                <li className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        setPromoDraft(WELCOME_PROMO_CODE);
                        void validatePromo(WELCOME_PROMO_CODE);
                      }}
                      className="rounded bg-white/10 px-2 py-1 font-mono text-sm font-semibold text-brand-200 transition hover:bg-white/20"
                    >
                      {WELCOME_PROMO_CODE}
                    </button>
                    <p className="mt-1 text-xs text-gray-400">Path 1 — Broker partner</p>
                  </div>
                </li>
                <li className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      type="button"
                      disabled
                      className="cursor-not-allowed rounded bg-white/10 px-2 py-1 font-mono text-sm font-semibold text-brand-200 opacity-60"
                    >
                      WLCMPB
                    </button>
                    <p className="mt-1 text-xs text-gray-400">Path 2 — Personal broker</p>
                    <p
                      className="mt-1 block w-fit rounded-lg border border-amber-400/55 bg-amber-400/15 px-2 py-1 text-xs font-medium text-amber-100"
                      role="status"
                    >
                      Temporarily disabled.
                    </p>
                  </div>
                </li>
              </ul>
              <p className="mt-3 border-t border-white/10 pt-3 text-xs text-gray-400">
                See full plans on the{" "}
                <Link
                  to="/Pricing"
                  className="font-medium text-brand-300 underline-offset-2 hover:underline"
                >
                  Pricing page
                </Link>
                .
              </p>
            </div>
          </aside>

          <section className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 shadow-xl">
          {slotName ? <SlotNameBadge name={slotName} className="mb-4" /> : null}
          {extendMode && currentExpiringDate ? (
            <div className="mb-4 rounded-xl border border-violet-400/35 bg-violet-500/10 px-4 py-3 text-sm text-violet-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-200/80">
                Extend subscription
              </p>
              <p className="mt-2">
                Plan chosen:{" "}
                <strong className="text-white">{selectedPlan.name}</strong> (+
                {planDaysForPlan(selectedPlan)} days)
              </p>
              <p className="mt-1">
                Current expiration:{" "}
                <strong className="text-white">
                  {formatExpiryDisplay(currentExpiringDate)}
                </strong>
              </p>
              {projectedExpiry ? (
                <p className="mt-1">
                  After payment:{" "}
                  <strong className="text-white">
                    {formatExpiryDisplay(projectedExpiry)}
                  </strong>
                </p>
              ) : null}
              <p className="mt-2 text-xs text-violet-200/90">
                Admin approval is required after payment, same as your first
                subscription.
              </p>
            </div>
          ) : null}
          {!awaitingTransfer &&
          paymentStatus === "pending" &&
          actuallyPaidUsdt != null &&
          Number(session?.plisioUnlockMinUsdt ?? session?.amountBep20) > 0 &&
          actuallyPaidUsdt + 0.0001 >=
            Number(session?.plisioUnlockMinUsdt ?? session?.amountBep20) ? (
            <div className="mb-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-center text-sm text-emerald-100">
              <p className="font-semibold text-emerald-200">
                {actuallyPaidUsdt} USDT detected on BSC
              </p>
              <p className="mt-2">Confirming with Plisio… this page updates automatically.</p>
            </div>
          ) : null}
          {awaitingTransfer ? (
            <div
              className={`mb-6 rounded-2xl border p-6 text-center shadow-xl ${
                paymentStatus === "confirmed"
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : paymentStatus === "awaiting_approval"
                    ? "border-amber-500/40 bg-amber-500/10"
                    : paymentStatus === "failed"
                    ? "border-red-500/40 bg-red-500/10"
                    : pendingExpired
                      ? "border-amber-500/40 bg-amber-500/10"
                      : "border-brand-400/30 bg-brand-500/10"
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Time remaining
              </p>
              <p
                className={`mt-2 font-mono text-4xl font-bold tabular-nums ${
                  pendingExpired ? "text-amber-300" : "text-white"
                }`}
              >
                {formatCountdown(msLeft)}
              </p>
              <p className="mt-4 text-sm font-medium text-gray-200">{pendingStatusLabel}</p>
              {paymentStatus === "confirmed" ? (
                <p className="mt-3 text-sm text-emerald-300">
                  {slotName
                    ? extendMode
                      ? `Extension payment confirmed for ${slotName} — redirecting to your subscription…`
                      : `Payment confirmed for slot ${slotName} — redirecting to your subscription…`
                    : "Payment confirmed — redirecting…"}
                </p>
              ) : paymentStatus === "awaiting_approval" ? (
                <p className="mt-3 text-sm text-amber-200">
                  {slotName
                    ? `Payment received for slot ${slotName}. You will be redirected to your subscription page automatically.`
                    : "Payment received. You will be redirected to your subscription page automatically."}
                </p>
              ) : null}
              {pendingExpired &&
              paymentStatus !== "confirmed" &&
              paymentStatus !== "awaiting_approval" ? (
                <button
                  type="button"
                  onClick={exitAwaitingTransfer}
                  className="mt-4 w-full rounded-xl bg-brand-500 py-3 text-sm font-bold text-white hover:bg-brand-600"
                >
                  Start a new payment
                </button>
              ) : paymentStatus !== "confirmed" && paymentStatus !== "awaiting_approval" ? (
                extendChooserUrl ? (
                  <Link
                    to={extendChooserUrl}
                    onClick={() => exitAwaitingTransfer()}
                    className="mt-4 inline-block text-sm text-gray-400 hover:text-brand-300"
                  >
                    ← Choose another plan
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={exitAwaitingTransfer}
                    className="mt-4 text-sm text-gray-400 hover:text-brand-300"
                  >
                    ← Change plan or promo
                  </button>
                )
              ) : null}
            </div>
          ) : null}

          {session.mock ? (
            <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100">
              <p className="font-semibold text-amber-200">
                Mock payment mode (dev)
              </p>
              <p className="mt-1 text-xs text-amber-100/90">
                Payment gateway is in mock mode (PAYMENTS_MOCK=1). No real
                transaction is needed — click the button to simulate a confirmed
                payment and unlock a slot.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={confirmingMock || regenerating || applyingPromo}
                  onClick={() => void confirmMockPayment()}
                  className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-amber-950 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {confirmingMock ? "Confirming…" : "Simulate payment confirmation"}
                </button>
                {mockMsg ? (
                  <span className="text-xs font-medium text-green-300">
                    {mockMsg}
                  </span>
                ) : null}
                {mockError ? (
                  <span className="text-xs font-medium text-red-300">
                    {mockError}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {!awaitingTransfer && !extendMode ? (
          <>
          <div className="mb-4">
            <label
              htmlFor="pay-plan"
              className="block text-xs font-semibold uppercase tracking-wide text-gray-400"
            >
              {extendMode ? "Choose plan to add" : "Choose your subscription"}
            </label>
            <select
              id="pay-plan"
              value={selectedPlanId}
              onChange={(e) => handlePlanChange(e.target.value)}
              disabled={applyingPromo || regenerating}
              className="mt-2 w-full rounded-xl border border-white/15 bg-[#0a102c] px-4 py-3 text-sm text-white outline-none ring-brand-400/30 focus:border-brand-400 focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {SLOT_PLANS.map((p) => (
                <option key={p.id} value={p.id} className="bg-[#0a102c] text-white">
                  {extendMode
                    ? `${p.name} — +${p.days} days (${p.regularPriceLabel})`
                    : `${p.name} — ${p.period} (${p.regularPriceLabel}${p.priceSuffix})`}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label
              htmlFor="pay-promo"
              className="block text-xs font-semibold uppercase tracking-wide text-gray-400"
            >
              Promo code
            </label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <input
                id="pay-promo"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={promoDraft}
                onChange={(e) => {
                  const v = e.target.value;
                  setPromoDraft(v);
                  if (!String(v).trim()) {
                    setPromoError(null);
                  }
                  if (applied && normalizePromo(v) !== normalizePromo(applied.code)) {
                    setApplied(null);
                    setPromoError(null);
                  }
                }}
                placeholder="Enter your promo code"
                className="w-full flex-1 rounded-xl border border-white/15 bg-[#0a102c] px-4 py-3 font-mono text-sm text-white outline-none ring-brand-400/30 placeholder:text-gray-500 focus:border-brand-400 focus:ring-2"
              />
              <button
                type="button"
                disabled={applyingPromo || regenerating}
                onClick={() => void validatePromo(promoDraft)}
                className="shrink-0 rounded-xl border border-brand-400/60 bg-brand-500/90 px-5 py-3 text-sm font-bold text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-45 sm:min-w-[9.5rem]"
              >
                {applyingPromo ? "Checking…" : applied ? "Applied" : "Apply"}
              </button>
            </div>
            {applyingPromo ? (
              <p className="mt-2 text-xs text-gray-400">Checking promo code…</p>
            ) : promoError ? (
              <p className="mt-2 text-xs font-medium text-red-300" role="alert">
                {promoError}
              </p>
            ) : applied ? (
              <p className="mt-2 text-xs font-medium text-emerald-300/95">
                {applied.infoMessage}
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-400">
                Enter a code and click <span className="text-gray-200">Apply</span> to update the
                price, or leave empty to pay the regular price.
              </p>
            )}
            {regenError ? (
              <p className="mt-2 text-xs font-medium text-red-300" role="alert">
                {regenError}
              </p>
            ) : null}
          </div>

          <div className="mb-6 space-y-2 rounded-xl border border-white/10 bg-[#0a102c] p-4 text-sm">
            <p className="flex items-center justify-between text-gray-300">
              <span>Regular price</span>
              <strong className="text-white">{session.regularPriceUsd} USD</strong>
            </p>
            <p className="flex items-center justify-between text-gray-300">
              <span>Plan price</span>
              <strong className="text-white">{session.planPriceUsd} USD</strong>
            </p>
            <p className="flex items-center justify-between text-emerald-300">
              <span>Discount</span>
              <strong>
                {discountUsd} USD ({discountPct}%)
              </strong>
            </p>

          </div>
          </>
          ) : null}

          {checkoutUrl && hideManualWallet ? (
            <div className="mb-6 rounded-xl border-2 border-red-500/50 bg-red-950/40 px-4 py-4 text-sm text-red-100">
              <p className="font-bold text-red-200">
                Do not pay by RedotPay Withdraw to a copied address
              </p>
              <p className="mt-2 leading-relaxed">
                RedotPay <strong>Withdraw</strong> often debits {sendAmountLabel} USDT but only ~
                {session.planPriceUsd} USDT or less lands on chain — then RedotPay{" "}
                <strong>credits your balance back</strong>. Use the {providerLabel} button instead.
              </p>
            </div>
          ) : null}

          <p className="text-center text-sm text-gray-300">
            {hideManualWallet
              ? `Plan price (${providerLabel} checkout):`
              : "Amount to enter in your wallet (RedotPay, etc.):"}
          </p>
          <div className="mt-4 text-center">
            <p className="text-3xl font-bold tracking-tight text-white">
              {hideManualWallet ? planPriceLabel : sendAmountLabel}{" "}
              <span className="text-lg font-semibold text-brand-300">USDT</span>
            </p>
            <p className="mt-2 text-xs text-gray-400">
              {hideManualWallet ? (
                <>
                  Plan <strong className="text-gray-200">{session.planPriceUsd} USDT</strong> — pay on{" "}
                  {providerLabel} ({net.chainStrong}).
                </>
              ) : (
                <>
                  We accept up to <strong className="text-gray-200">{networkFeeMax} USDT</strong>{" "}
                  network fee. If less than{" "}
                  <strong className="text-gray-200">{session.amountBep20} USDT</strong> arrives,
                  payment fails.
                </>
              )}
            </p>
          </div>

          {!hideManualWallet ? (
            <>
              <div className="mt-8 flex flex-col items-center border-t border-white/10 pt-6">
                {qrImageUrl ? (
                  <img
                    src={qrImageUrl}
                    alt="Deposit address QR"
                    className="rounded-xl border border-white/10 bg-white p-2"
                    width={240}
                    height={240}
                  />
                ) : null}
              </div>

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {net.depositLabel}
                </p>
                <p
                  className="mt-2 select-all break-all rounded-lg border border-white/10 bg-[#0a102c] p-3 font-mono text-sm leading-snug text-gray-100"
                  title={active.payAddress}
                >
                  {active.payAddress}
                </p>
                {!addressOk ? (
                  <p className="mt-2 text-xs text-amber-300">
                    Unusual address format — please double-check before sending.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => copyText("address", active.payAddress)}
                  className="mt-2 text-sm font-medium text-brand-300 underline-offset-2 hover:underline"
                >
                  Copy address only
                </button>
              </div>
            </>
          ) : null}

          {copied ? (
            <p className="mt-3 text-center text-sm text-emerald-300">Copied: {copied}</p>
          ) : null}

          {!awaitingTransfer ? (
          <div className="mt-8 flex flex-col items-center border-t border-white/10 pt-6">
            <button
              type="button"
              disabled={
                applyingPromo ||
                bootstrapping ||
                regenerating ||
                (!plisioEmbedded && !addressOk && !checkoutUrl)
              }
              onClick={proceedToPay}
              className="w-full max-w-md rounded-xl bg-brand-500 px-6 py-4 text-base font-bold text-white shadow-lg transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {regenerating
                ? "Updating…"
                : plisioEmbedded
                  ? "Pay and check status"
                  : checkoutUrl
                    ? `Pay on ${providerLabel} (required)`
                    : "Continue to payment"}
            </button>
          </div>
          ) : !hideManualWallet ? (
          <div className="mt-6 border-t border-white/10 pt-6">
            <button
              type="button"
              onClick={() =>
                void copyText(
                  "payment",
                  [
                    `Amount to enter: ${sendAmountLabel} USDT`,
                    `Minimum received: ${session.amountBep20} USDT`,
                    "Network: BNB Smart Chain (BEP20 / BSC)",
                    `Address: ${active.payAddress}`,
                    paymentId ? `Payment ID: ${paymentId}` : null,
                  ]
                    .filter(Boolean)
                    .join("\n"),
                )
              }
              className="w-full rounded-xl border border-white/20 bg-white/5 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              {copied === "payment" ? "Copied" : "Copy amount + address"}
            </button>
          </div>
          ) : null}

          </section>
        </div>

        <p className="text-center text-sm text-gray-400">
          <Link to="/client/dashboard" className="text-brand-300 hover:underline">
            Back to Dashboard
          </Link>
        </p>
      </div>

    </main>
  );
}
